import { Box, styled, SxProps, Theme } from '@mui/material';
import classNames from 'classnames';
import {
  CSSProperties,
  type MouseEventHandler,
  type ReactNode,
  type TransitionEventHandler,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useResizeObserver } from 'usehooks-ts';

import { useAppBehavior, useIsIdle } from '../data-context';

type Dir = 1 | -1;

const PREFIX = 'TSMED-Carousel';

export const carouselClasses = {
  header: `${PREFIX}-header`,
  carousel: `${PREFIX}-carousel`,
  carouselWrapper: `${PREFIX}-carouselWrapper`,
  carouselContent: `${PREFIX}-carouselContent`,
  carouselItem: `${PREFIX}-carouselItem`,
};

interface RootProps {
  itemWidth: number;
  itemHeight: number;
  gap: number;
  scrollStepSize: number;
  normalSize: number;
  normalStretch: boolean;
  normalMinSize?: number;
  isHorizontal?: boolean;
  isFullHeight: boolean;
}

const excludedRootProps: PropertyKey[] = [
  'itemWidth',
  'itemHeight',
  'itemSize',
  'gap',
  'scrollStepSize',
  'normalSize',
  'normalStretch',
  'normalMinSize',
  'isHorizontal',
  'isFullHeight',
];

const Root = styled(Box, {
  shouldForwardProp: prop => !excludedRootProps.includes(prop),
})<RootProps>(
  ({
    theme: { spacing, palette },
    itemWidth,
    itemHeight,
    gap,
    normalSize,
    normalStretch,
    normalMinSize,
    isHorizontal = true,
    isFullHeight,
  }) => ({
    display: 'flex',
    fontFamily: "'JetBrains Mono', monospace",
    maxHeight: '100%',
    width: '100%',

    [`.${carouselClasses.header}`]: {
      writingMode: 'sideways-lr',
      display: 'flex',
      justifyContent: 'end',
      padding: spacing(2, 1),
    },

    [`& .${carouselClasses.carousel}`]: {
      display: 'flex',
      width: '100%',
      gap: 8,
      position: 'relative',
      overflow: 'hidden',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },

      boxSizing: 'border-box',
      ...(isHorizontal
        ? { flexDirection: 'column', flex: 1 }
        : { flexDirection: 'row', flex: `0 0 ${itemWidth}px` }),
      minHeight: 0,
      padding: 0,

      // orientation-aware shadows
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: 1,
        opacity: 0,
        ...(isHorizontal
          ? {
              width: spacing(1),
            }
          : {
              height: spacing(1),
            }),
      },

      '&.scroll-right::after, &.scroll-bottom::after': { opacity: 1 },
      '&.scroll-left::before, &.scroll-top::before': { opacity: 1 },

      ...(isHorizontal
        ? {
            '&::before': {
              top: 0,
              bottom: 0,
              left: 0,
              background: `linear-gradient(to right, ${palette.background.default} 0%, rgba(0,0,0,0) 100%)`,
            },
            '&::after': {
              top: 0,
              bottom: 0,
              right: 0,
              background: `linear-gradient(to left, ${palette.background.default} 0%, rgba(0,0,0,0) 100%)`,
            },
          }
        : {
            '&::before': {
              left: 0,
              right: 0,
              top: 0,
              background: `linear-gradient(to bottom, ${palette.background.default} 0%, rgba(0,0,0,0) 100%)`,
            },
            '&::after': {
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to top, ${palette.background.default} 0%, rgba(0,0,0,0) 100%)`,
            },
          }),

      [`.${carouselClasses.carouselContent}`]: {
        display: 'grid',

        // --- orientation branches ---
        ...(isHorizontal
          ? {
              // HORIZONTAL
              gridTemplateRows: `repeat(${normalSize}, minmax(${itemHeight}px, 1fr))`,
              gridTemplateColumns: `repeat(auto-fill, ${itemWidth}px)`,
              gridAutoColumns: `${itemWidth}px`,
              gridAutoFlow: 'column',
              touchAction: 'pan-x pinch-zoom',
            }
          : {
              // VERTICAL
              gridTemplateRows: `repeat(${normalSize}, minmax(${itemHeight}px, 1fr))`,
              gridAutoRows: `${itemHeight}px`,
              gridTemplateColumns: `${itemWidth}px`,
              gridAutoFlow: 'row',
              touchAction: 'pan-y pinch-zoom',
            }),

        overscrollBehavior: 'contain',

        columnGap: `${gap}px`,
        rowGap: `${gap}px`,
        position: 'relative',
        padding: 0,
        margin: 0,
        listStyle: 'none',

        // Keep native scroll for both modes (we'll drive scrollLeft/Top programmatically)
        ...(isHorizontal
          ? { overflowX: 'auto', overscrollBehaviorX: 'contain' }
          : { overflowY: 'auto', overscrollBehaviorY: 'contain' }),
        scrollBehavior: 'auto',
        transform: `translate3d(0,0,0)`,
        WebkitOverflowScrolling: 'auto',

        scrollbarWidth: 'none',
        scrollbarHeight: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...(isFullHeight ? { height: '100%' } : {}),
        width: '100%',

        [`.${carouselClasses.carouselItem}`]: {
          width: `${itemWidth}px`,
          height: `${itemHeight}px`,
          boxSizing: 'border-box',
          minWidth: 0,
          minHeight: 0,
        },

        ...(normalStretch &&
          (isHorizontal
            ? {
                gridTemplateRows: `repeat(${normalSize}, minmax(${Math.floor(normalMinSize ?? itemHeight)}px, 1fr))`,
                [`.${carouselClasses.carouselItem}`]: {
                  height: '100%',
                  minHeight: 0,
                  '& > *': {
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    minHeight: 0,
                  },
                },
              }
            : {
                height: '100%',
                gridTemplateRows: `repeat(${normalSize}, minmax(${Math.floor(normalMinSize ?? itemHeight)}px, 1fr))`,
                [`.${carouselClasses.carouselItem}`]: {
                  height: '100%',
                  minHeight: 0,
                  '& > *': {
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    minHeight: 0,
                  },
                },
              })),
      },
    },
  }),
);

// ---- constants ----
const HANDLE_WHEEL_TRANSITION_MS = 300; // fast animation on wheel
const EPS = 1;

interface CarouselProps<T> {
  items: ReadonlyArray<T>;
  renderItem: (item: T) => ReactNode;
  itemKey: keyof T;

  /**
   * Reflects the spread or width across the non-dominant axis.
   * In horizontal mode, this represents the number of rows.
   * In vertical mode, this represents the number of columns.
   * @default 1
   */
  normalSize?: number;

  /**
   * Reflects the spread or width across the non-dominant axis.
   * In horizontal mode, this represents the number of rows.
   * In vertical mode, this represents the number of columns.
   * @default 1
   */
  normalMinSize?: number; // minimum row height (px) used when stretchRows is true

  /**
   * Width in horizontal mode, or height in vertical mode, of each item in pixels.
   * @default 300
   */
  itemWidth?: number;
  itemHeight?: number;
  gap?: number;

  /**
   * Number of items to scroll when navigating (arrows, auto-scroll, etc).
   * @default 1
   */
  scrollStepSize?: number;
  onResize?: (el: HTMLUListElement) => void;
  highlightDuration?: number;
  onScrollEnd?: () => void;
  onMouseEnter?: MouseEventHandler<HTMLUListElement>;
  onMouseLeave?: MouseEventHandler<HTMLUListElement>;
  onLoopNext?: (listener: (event: CustomEvent) => void) => () => void;
  loopInterval?: number;
  transitionDuration?: number;
  sx?: SxProps<Theme> | undefined;

  /**
   * When true, rows expand to fill the container height.
   * This requires that the container has a defined height.
   * @default false
   */
  normalStretch?: boolean;
  variant?: 'vertical' | 'horizontal';
  isFullHeight?: boolean;

  /**
   * When true, disables all scrolling, animations, and event listeners.
   * Useful for preview mode in layout settings.
   * @default false
   */
  disabled?: boolean;
}

// eslint-disable-next-line comma-spacing
export const Carousel = <T,>({
  items,
  renderItem,
  itemKey,
  normalSize: normalHeight = 1,
  itemWidth = 300,
  itemHeight = 140,
  gap = 8,
  scrollStepSize = 1,
  onResize,
  transitionDuration = 5000,
  sx,
  normalStretch = false,
  normalMinSize,
  variant = 'horizontal',
  isFullHeight = true,
  disabled = false,
}: CarouselProps<T>) => {
  const id = useId();
  const idRef = useRef<string>(id);
  const ulRef = useRef<HTMLUListElement>(null);
  const isAnimatingRef = useRef(false);
  const directionRef = useRef<Dir>(1);
  const [baseItems, setBaseItems] = useState(items);
  const pendingItemsRef = useRef<typeof items | null>(null);
  const lastScrollPosRef = useRef<number>(0);
  const [scrollDirection, setScrollDirection] = useState<
    'left' | 'right' | 'top' | 'bottom' | null
  >(variant === 'horizontal' ? 'right' : null);

  const isIdle = useIsIdle();
  const isHorizontal = variant === 'horizontal';
  const { behaviorEventEmitter } = useAppBehavior();

  // Update items only when not animating
  useEffect(() => {
    // When disabled, just sync items directly without animation tracking
    if (disabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional sync for disabled mode
      setBaseItems(items);
      return;
    }
    if (!isAnimatingRef.current) {
      // Sync items state with external items prop when not animating

      setBaseItems(items);
      pendingItemsRef.current = null;
    } else {
      pendingItemsRef.current = items;
    }
  }, [items, disabled]);

  const safeNormal = Math.max(
    1,
    Number.isFinite(normalHeight) ? Math.floor(normalHeight) : 1,
  );
  const safeCardinalPerScroll = Math.max(
    1,
    Number.isFinite(scrollStepSize) ? Math.floor(scrollStepSize) : 1,
  );

  // ---- metrics helpers ----
  const getOneCardinalPx = useCallback(() => {
    const cs = ulRef.current ? getComputedStyle(ulRef.current) : null;
    const gapPx = cs
      ? parseFloat(
          cs.getPropertyValue(isHorizontal ? 'column-gap' : 'row-gap') ||
            `${gap}`,
        )
      : gap;
    const itemSize = isHorizontal ? itemWidth : itemHeight;
    return itemSize + gapPx;
  }, [itemWidth, itemHeight, gap, isHorizontal]);

  const getPeekPx = useCallback(() => {
    const cs = ulRef.current ? getComputedStyle(ulRef.current) : null;
    return cs ? parseFloat(cs.getPropertyValue('--carousel-peek') || '0') : 0;
  }, []);

  // clamp left within [maxLeft, 0]
  const getBounds = useCallback(() => {
    const ul = ulRef.current;
    if (!ul) {
      return { minStart: 0, maxStart: 0 };
    }
    const minStart = 0;
    const maxStart =
      -(isHorizontal
        ? ul.scrollWidth - ul.clientWidth
        : ul.scrollHeight - ul.clientHeight) + getPeekPx();
    return { minStart, maxStart: Math.min(0, maxStart) };
  }, [getPeekPx, isHorizontal]);

  // step = columnsPerScroll * oneColumn
  const getStepPx = useCallback(
    () => getOneCardinalPx() * Math.max(1, safeCardinalPerScroll),
    [getOneCardinalPx, safeCardinalPerScroll],
  );

  const nearly = (a: number, b: number, eps = EPS) => Math.abs(a - b) <= eps;

  // animate scrollLeft/Top to target over duration
  const animateScrollTo = useCallback(
    (target: number, duration: number) => {
      const ul = ulRef.current;
      if (!ul) {
        return;
      }

      const prop = isHorizontal ? 'scrollLeft' : ('scrollTop' as const);
      const start = ul[prop];
      const change = target - start;
      if (Math.abs(change) < 0.5 || duration <= 0) {
        ul[prop] = target;
        return;
      }

      isAnimatingRef.current = true;
      const startTime = performance.now();

      const step = (t: number) => {
        const elapsed = t - startTime;
        const progress = Math.min(1, elapsed / duration);
        // ease-in-out
        const eased = 0.5 - 0.5 * Math.cos(Math.PI * progress);
        ul[prop] = start + change * eased;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          isAnimatingRef.current = false;
          // apply pending items if any
          if (pendingItemsRef.current) {
            setBaseItems(pendingItemsRef.current);
            pendingItemsRef.current = null;
          }
          // emit edges if needed
          const { minStart, maxStart } = getBounds();
          const current = -ul[prop];
          if (directionRef.current === 1 && nearly(current, maxStart)) {
            behaviorEventEmitter.emit({
              type: 'carousel-report-edge',
              id: idRef.current,
              dir: 1,
            });
          } else if (directionRef.current === -1 && nearly(current, minStart)) {
            behaviorEventEmitter.emit({
              type: 'carousel-report-edge',
              id: idRef.current,
              dir: -1,
            });
          }
        }
      };

      requestAnimationFrame(step);
    },
    [behaviorEventEmitter, getBounds, isHorizontal],
  );

  // programmatic move by scrolling (no left/top)
  const moveCardinal = useCallback(
    (dir: Dir, duration = transitionDuration) => {
      const ul = ulRef.current;
      if (!ul || isAnimatingRef.current) {
        return;
      }

      directionRef.current = dir;

      const stepPx = getStepPx();
      const { minStart, maxStart } = getBounds();

      const currentScroll = isHorizontal ? ul.scrollLeft : ul.scrollTop;
      const currentStart = -currentScroll;
      const nextStartRaw = currentStart - dir * stepPx;
      const nextStart = Math.max(maxStart, Math.min(minStart, nextStartRaw));
      const targetScroll = -nextStart;

      // already at edge?
      if (
        (dir === 1 && nearly(currentStart, maxStart)) ||
        (dir === -1 && nearly(currentStart, minStart))
      ) {
        // Switch shadow direction to the opposite
        if (isHorizontal) {
          setScrollDirection(dir === 1 ? 'left' : 'right');
        } else {
          setScrollDirection(dir === 1 ? 'top' : 'bottom');
        }
        behaviorEventEmitter.emit({
          type: 'carousel-report-edge',
          id: idRef.current,
          dir,
        });
        return;
      }

      animateScrollTo(targetScroll, duration);
    },
    [
      animateScrollTo,
      behaviorEventEmitter,
      getBounds,
      getStepPx,
      isHorizontal,
      transitionDuration,
    ],
  );

  // Wrapper on manual scroll to detect direction & edges
  const handleManualScroll = useCallback(() => {
    // Skip scroll handling when disabled (preview mode)
    if (disabled) {
      return;
    }
    const ul = ulRef.current;
    if (!ul) {
      return;
    }

    const current = isHorizontal ? ul.scrollLeft : ul.scrollTop;
    const last = lastScrollPosRef.current;

    // Determine direction
    if (current > last) {
      setScrollDirection(isHorizontal ? 'right' : 'bottom');
    } else if (current < last) {
      setScrollDirection(isHorizontal ? 'left' : 'top');
    }

    lastScrollPosRef.current = current;

    // Edge detection: flip direction if at edge
    const { minStart, maxStart } = getBounds();
    const currentStart = -current;
    if (
      (isHorizontal && currentStart <= maxStart + EPS) ||
      (!isHorizontal && currentStart <= maxStart + EPS)
    ) {
      setScrollDirection(isHorizontal ? 'left' : 'top');
    }
    if (
      (isHorizontal && currentStart >= minStart - EPS) ||
      (!isHorizontal && currentStart >= minStart - EPS)
    ) {
      setScrollDirection(isHorizontal ? 'right' : 'bottom');
    }
  }, [disabled, getBounds, isHorizontal]);

  // kept for API compatibility (no longer used for CSS left/top transitions)
  const handleScrollPositionChangeEnd: TransitionEventHandler<
    HTMLUListElement
  > = () => {};

  useEffect(() => {
    // Skip registration when disabled (preview mode)
    if (disabled) {
      return;
    }
    const id = idRef.current;

    behaviorEventEmitter.emit({
      type: 'carousel-register',
      id,
      canMove: true,
      variant: isHorizontal ? 'horizontal' : 'vertical',
    });
    return () => {
      behaviorEventEmitter.emit({
        type: 'carousel-update-can-move',
        id,
        canMove: false,
      });
    };
  }, [behaviorEventEmitter, isHorizontal, disabled]);

  // ---- subscribe to global ticks ----
  useEffect(() => {
    // Skip subscription when disabled (preview mode)
    if (disabled) {
      return;
    }
    const unsubscribe = behaviorEventEmitter.addListener('carousel-tick', e => {
      const target = e.detail?.targetOrientation;

      if (target && target !== (isHorizontal ? 'horizontal' : 'vertical')) {
        return;
      }

      if (isAnimatingRef.current) {
        return;
      }
      const dir = e.detail.dir as Dir;
      const { minStart, maxStart } = getBounds();
      const ul = ulRef.current;
      if (!ul) {
        return;
      }
      const currentStart = -(isHorizontal ? ul.scrollLeft : ul.scrollTop);
      if (
        (dir === 1 && currentStart > maxStart + EPS) ||
        (dir === -1 && currentStart < minStart - EPS)
      ) {
        moveCardinal(dir);
      } else {
        behaviorEventEmitter.emit({
          type: 'carousel-report-edge',
          id: idRef.current,
          dir,
        });
      }
    });
    return () => unsubscribe();
  }, [behaviorEventEmitter, getBounds, moveCardinal, isHorizontal, disabled]);

  // ---- initial & responsive left clamp ----
  useLayoutEffect(() => {
    // Skip scroll position adjustments when disabled (preview mode)
    if (disabled) {
      return;
    }
    const ul = ulRef.current;
    if (!ul) {
      return;
    }

    const { minStart, maxStart } = getBounds();
    const currentStart = -(isHorizontal ? ul.scrollLeft : ul.scrollTop);
    const clamped = Math.max(maxStart, Math.min(minStart, currentStart));
    const targetScroll = -clamped;
    if (isHorizontal) {
      ul.scrollLeft = targetScroll;
    } else {
      ul.scrollTop = targetScroll;
    }
  }, [
    disabled,
    baseItems.length,
    itemWidth,
    gap,
    safeNormal,
    getBounds,
    isHorizontal,
  ]);

  const refreshPosition = () => {
    // Skip refresh when disabled (preview mode)
    if (disabled) {
      return;
    }
    if (ulRef.current) {
      onResize?.(ulRef.current);
    }
  };

  useResizeObserver({
    ref: ulRef as React.RefObject<HTMLElement>,
    onResize: refreshPosition,
  });

  // Wheel: manual scrolling stays native; when idle, wheel nudges auto move
  useEffect(() => {
    // Skip wheel listener when disabled (preview mode)
    if (disabled) {
      return;
    }
    const ul = ulRef.current;
    if (!ul) {
      return;
    }

    const norm = (v: number, mode: number) => {
      if (mode === 1) {
        return v * 16;
      }
      if (mode === 2) {
        return v * (isHorizontal ? ul.clientWidth : ul.clientHeight);
      }
      return v;
    };

    const onWheel = (e: WheelEvent) => {
      const dx = norm(e.deltaX, e.deltaMode);
      const dy = norm(e.deltaY, e.deltaMode);
      const dominant = Math.abs(dx) > Math.abs(dy) ? dx : dy;

      // User ACTIVE → manual scroll (translate wheel to the correct axis)
      if (!isIdle) {
        e.preventDefault(); // ensure parents (e.g., map) don't steal the wheel
        if (isHorizontal) {
          ul.scrollLeft += dominant;
        } else {
          ul.scrollTop += dominant;
        }
        return;
      }

      // User IDLE → nudge auto-scroll
      if (!Number.isFinite(dominant)) {
        return;
      }
      e.preventDefault();
      const dir: Dir = dominant > 0 ? 1 : -1;
      behaviorEventEmitter.emit({ type: 'carousel-force-direction', dir });
      moveCardinal(dir, HANDLE_WHEEL_TRANSITION_MS);
    };

    ul.addEventListener('wheel', onWheel, { passive: false });
    return () => ul.removeEventListener('wheel', onWheel);
  }, [disabled, isIdle, moveCardinal, behaviorEventEmitter, isHorizontal]);

  return (
    <Root
      sx={sx}
      gap={gap}
      // Floor, never round: itemHeight can be a fractional exact-fit value
      // and feeds the grid row MINIMUM (minmax). Rounding up makes the sum of
      // row minimums exceed the container and clips the bottom row (TMDXNC-18).
      itemHeight={Math.floor(itemHeight)}
      itemWidth={Math.round(itemWidth)}
      scrollStepSize={safeCardinalPerScroll}
      normalSize={safeNormal}
      normalStretch={normalStretch}
      normalMinSize={normalMinSize}
      isHorizontal={isHorizontal}
      isFullHeight={isFullHeight}
    >
      <Box
        className={classNames(
          carouselClasses.carousel,
          scrollDirection && `scroll-${scrollDirection}`,
        )}
      >
        <ul
          ref={ulRef}
          className={classNames(carouselClasses.carouselContent)}
          onTransitionEnd={handleScrollPositionChangeEnd}
          onScroll={handleManualScroll}
          style={
            {
              ...(normalMinSize
                ? { ['--carousel-row-min']: `${normalMinSize}px` }
                : {}),
            } as CSSProperties
          }
        >
          {baseItems.map((item, index) => (
            <li
              key={String(item?.[itemKey] ?? `item-${index}`)}
              className={classNames(carouselClasses.carouselItem)}
              data-rows={safeNormal}
              data-row-index={index % safeNormal}
              data-column={Math.floor(index / safeNormal)}
            >
              {item && renderItem(item)}
            </li>
          ))}
        </ul>
      </Box>
    </Root>
  );
};
